import type {
  Page, PageVersion, MediaItem,
  SiteSettings, User, PublishLog, FormSubmission, SubmissionStats, VisitStats,
  CollectionTypeDef, CollectionItem, OnboardingCatalog, ChecklistStatus,
  BookingRecord, BookingSchedule, BookingService, BookingStaff, BookingStaffGroup,
  FeedbackRequestRecord, FeedbackResponseRecord, TestimonialRecord, FeedbackSummary, FeedbackSettingsRecord,
  QrCodeRecord,
  GoogleBusinessRecord, GooglePlaceCandidate,
  ChatbotSettingsRecord, ChatbotSettingsInput, ChatbotUsageSummary,
  ChatbotConversationSummary, ChatbotMessageRecord,
  CloudflareDomainStatus, DomainSearchResult, DomainCheckResult, DomainConnectResult,
  RedirectPresetState, TutorialProgressState,
  LegalDoc, LegalDocKey, LegalDocLocale,
  LinksResponse, LinksLocaleState, BioProps,
  AnnouncementRecord, AnnouncementInput,
  AssistantEventInput, ActivityEntry, AssistantExecuteResult,
  AiImageGenerateInput, AiImageGenerateResult,
} from './types';
import type { AiSettings, AiPrompts } from '@palacete/shared/ai-settings';

const BASE = '/api';

// Valores efectivos por defecto (entorno + código) que el panel muestra como
// referencia/placeholder cuando el SaaS owner no ha personalizado un campo.
export interface AiSettingsDefaults {
  textModel: string;
  imageModel: string;
  imageEnabled: boolean;
  textDailyLimit: number;
  imageMonthlyLimit: number;
  imageDefaultSize: string;
  imageDefaultQuality: string;
  imageDefaultBackground: string;
  prompts: AiPrompts;
  industryStyleHints: Record<string, string>;
  industryCopyHints: Record<string, string>;
}

export interface AiSettingsResponse {
  settings: AiSettings;
  defaults: AiSettingsDefaults;
  hasApiKey: boolean;
}

// Tenant activo para saas_admin. Se persiste en localStorage para sobrevivir recargas.
let _activeTenantId: string | null = (() => {
  try { return localStorage.getItem('pal_tenant'); } catch { return null; }
})();

// Capacidades del plan cacheadas a nivel de módulo (se invalidan al cambiar de tenant).
let _capsPromise: Promise<PanelCapabilities> | null = null;

export function clearActiveTenantId(): void {
  _activeTenantId = null;
  _capsPromise = null;
  try { localStorage.removeItem('pal_tenant'); } catch { /* ignore */ }
}

export function setActiveTenantId(id: string | null): void {
  _activeTenantId = id;
  _capsPromise = null;
  try {
    if (id) localStorage.setItem('pal_tenant', id);
    else localStorage.removeItem('pal_tenant');
  } catch { /* ignore */ }
}

export interface PanelCapabilities {
  plan: string;
  locales: string[];
  allowedLocales: string[];
  multilingual: boolean;
  aiImages: boolean;
}

// Capacidades del plan, cacheadas una vez por tenant activo.
export function capabilitiesCached(): Promise<PanelCapabilities> {
  if (!_capsPromise) _capsPromise = api.settings.capabilities();
  return _capsPromise;
}

export function getActiveTenantId(): string | null {
  return _activeTenantId;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const suffix = _activeTenantId
    ? (path.includes('?') ? '&' : '?') + `tenantId=${encodeURIComponent(_activeTenantId)}`
    : '';
  const res = await fetch(`${BASE}${path}${suffix}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error: string }).error ?? res.statusText);
  }
  return res.json();
}

async function authReq<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
    credentials: 'include',
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error((err as { error: string }).error ?? res.statusText);
  }
  return res.json();
}

// Pages
export const api = {
  // Configuración global de IA (solo SaaS owner).
  admin: {
    getAiSettings: () => req<AiSettingsResponse>('/admin/ai'),
    saveAiSettings: (body: AiSettings) =>
      req<{ ok: boolean; settings: AiSettings }>('/admin/ai', { method: 'PUT', body: JSON.stringify(body) }),
  },
  bookings: {
    list: (from?: string, to?: string) => {
      const qs = new URLSearchParams(); if (from) qs.set('from', from); if (to) qs.set('to', to);
      return req<BookingRecord[]>(`/bookings${qs.size ? `?${qs}` : ''}`);
    },
    services: () => req<BookingService[]>('/bookings/services'),
    createService: (body: Partial<BookingService> & { name: string; durationMinutes: number }) => req<BookingService>('/bookings/services', { method: 'POST', body: JSON.stringify(body) }),
    updateService: (id: string, body: Partial<BookingService>) => req<BookingService>(`/bookings/services/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    groups: () => req<BookingStaffGroup[]>('/bookings/groups'),
    createGroup: (body: { name: string; description?: string; staffIds: string[] }) => req<BookingStaffGroup>('/bookings/groups', { method: 'POST', body: JSON.stringify(body) }),
    updateGroup: (id: string, body: Partial<BookingStaffGroup>) => req<BookingStaffGroup>(`/bookings/groups/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteGroup: (id: string) => req<{ ok: boolean }>(`/bookings/groups/${id}`, { method: 'DELETE' }),
    staff: () => req<BookingStaff[]>('/bookings/staff'),
    createStaff: (body: { name: string; email?: string; officeId?: string; collectionItemId?: string; timezone?: string; serviceIds: string[] }) => req<BookingStaff>('/bookings/staff', { method: 'POST', body: JSON.stringify(body) }),
    updateStaff: (id: string, body: { name: string; email?: string; officeId?: string; timezone?: string; serviceIds: string[] }) => req<BookingStaff>(`/bookings/staff/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    deleteStaff: (id: string) => req<{ ok: boolean } | { error: string; blockingReason: 'active_bookings' | 'historical_bookings' }>(`/bookings/staff/${id}`, { method: 'DELETE' }),
    schedule: (staffId: string) => req<BookingSchedule>(`/bookings/staff/${staffId}/schedule`),
    saveSchedule: (staffId: string, rules: BookingSchedule['rules']) => req<{ ok: boolean }>(`/bookings/staff/${staffId}/schedule`, { method: 'PUT', body: JSON.stringify({ rules }) }),
    addTimeOff: (staffId: string, body: { startsAt: string; endsAt: string; reason?: string }) => req(`/bookings/staff/${staffId}/time-off`, { method: 'POST', body: JSON.stringify(body) }),
    deleteTimeOff: (staffId: string, id: string) => req<{ ok: boolean }>(`/bookings/staff/${staffId}/time-off/${id}`, { method: 'DELETE' }),
    setStatus: (id: string, status: BookingRecord['booking']['status']) => req<{ ok: boolean }>(`/bookings/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  },
  feedback: {
    summary: () => req<FeedbackSummary>('/feedback/summary'),
    settings: () => req<FeedbackSettingsRecord>('/feedback/settings'),
    updateSettings: (body: FeedbackSettingsRecord) => req<FeedbackSettingsRecord>('/feedback/settings', { method: 'PUT', body: JSON.stringify(body) }),
    requests: () => req<FeedbackRequestRecord[]>('/feedback/requests'),
    createRequest: (body: {
      bookingId?: string;
      customerName?: string;
      customerEmail?: string;
      customerPhone?: string;
      channel?: 'link' | 'email' | 'whatsapp';
      scoreType?: 'csat' | 'nps';
    }) => req<FeedbackRequestRecord>('/feedback/requests', { method: 'POST', body: JSON.stringify(body) }),
    sendRequest: (id: string) => req<{ ok: boolean; request: FeedbackRequestRecord | null; sharePath: string | null; shareUrl: string | null }>(`/feedback/requests/${id}/send`, { method: 'POST' }),
    responses: () => req<FeedbackResponseRecord[]>('/feedback/responses'),
    reviewResponse: (id: string, status: 'reviewed' | 'archived') => req<{ ok: boolean; status: string }>(`/feedback/responses/${id}/review`, { method: 'POST', body: JSON.stringify({ status }) }),
  },
  testimonials: {
    list: () => req<TestimonialRecord[]>('/testimonials'),
    create: (body: {
      feedbackResponseId?: string;
      quote?: string;
      authorName?: string;
      authorRole?: string;
      authorLocation?: string;
      ratingValue?: number | null;
    }) => req<TestimonialRecord>('/testimonials', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: {
      quote: string;
      authorName: string;
      authorRole?: string;
      authorLocation?: string;
      ratingValue?: number | null;
    }) => req<TestimonialRecord>(`/testimonials/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    approve: (id: string) => req<TestimonialRecord>(`/testimonials/${id}/approve`, { method: 'POST' }),
    reject: (id: string) => req<TestimonialRecord>(`/testimonials/${id}/reject`, { method: 'POST' }),
    feature: (id: string, featured: boolean) => req<TestimonialRecord>(`/testimonials/${id}/feature`, { method: 'POST', body: JSON.stringify({ featured }) }),
  },
  legal: {
    get: () => req<LegalDoc[]>('/legal'),
    fromTemplate: (docKey: LegalDocKey, locale: 'es' | 'en') =>
      req<{ title: string; body: string }>(`/legal/${docKey}/from-template`, { method: 'POST', body: JSON.stringify({ locale }) }),
    save: (docKey: LegalDocKey, body: { locale: 'es' | 'en'; title?: string; body: string; publish?: boolean }) =>
      req<LegalDocLocale>(`/legal/${docKey}`, { method: 'PUT', body: JSON.stringify(body) }),
  },
  links: {
    get: () => req<LinksResponse>('/links'),
    save: (body: { locale: 'es' | 'en'; props: BioProps; publish?: boolean }) =>
      req<LinksLocaleState>('/links', { method: 'PUT', body: JSON.stringify(body) }),
  },
  announcements: {
    list: () => req<AnnouncementRecord[]>('/announcements'),
    create: (body: AnnouncementInput) =>
      req<AnnouncementRecord>('/announcements', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: AnnouncementInput) =>
      req<AnnouncementRecord>(`/announcements/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    remove: (id: string) =>
      req<{ ok: boolean }>(`/announcements/${id}`, { method: 'DELETE' }),
  },

  pages: {
    list: () => req<Page[]>('/pages'),
    get: (id: string) => req<Page>(`/pages/${id}`),
    getDraft: (id: string) => req<{ pageId: string; tenantId: string; contentJson: string; updatedAt: Date | null }>(`/pages/${id}/draft`),
    create: (body: { slug: string; locale: string; title: string; pageKey?: string }) =>
      req<Page>('/pages', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: Partial<Page>) =>
      req<Page>(`/pages/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    saveDraft: (id: string, contentJson: string) =>
      req<{ pageId: string; tenantId: string; contentJson: string; updatedAt: Date | null }>(`/pages/${id}/draft`, { method: 'PUT', body: JSON.stringify({ contentJson }) }),
    saveContent: (id: string, contentJson: string) =>
      req<Page>(`/pages/${id}/content`, { method: 'PUT', body: JSON.stringify({ contentJson }) }),
    versions: (id: string) => req<PageVersion[]>(`/pages/${id}/versions`),
    delete: (id: string) => req<{ ok: boolean }>(`/pages/${id}`, { method: 'DELETE' }),
    // Returns the sibling locale page (same page_key, different locale), or null
    getSibling: async (page: import('./types').Page): Promise<import('./types').Page | null> => {
      if (!page.pageKey) return null;
      const all = await req<import('./types').Page[]>('/pages');
      return all.find((p) => p.pageKey === page.pageKey && p.locale !== page.locale) ?? null;
    },
  },

  // Motor de colecciones genéricas — reemplaza a properties/agents/posts.
  collectionTypes: {
    list: () => req<CollectionTypeDef[]>('/collection-types'),
    get: (key: string) => req<CollectionTypeDef>(`/collection-types/${key}`),
    updateLabels: (key: string, labels: { singular?: { es?: string; en?: string }; plural?: { es?: string; en?: string } }) =>
      req<{ ok: boolean; labels: { singular: Record<string, string>; plural: Record<string, string> } }>(
        `/collection-types/${key}/labels`, { method: 'PATCH', body: JSON.stringify(labels) }),
  },

  collections: {
    // En el panel listamos todo (draft + published); el sitio filtra published.
    list: (key: string, opts?: { status?: 'draft' | 'published'; featured?: '0' | '1'; locale?: string }) => {
      const qs = new URLSearchParams();
      if (opts?.status) qs.set('status', opts.status);
      if (opts?.featured) qs.set('featured', opts.featured);
      if (opts?.locale) qs.set('locale', opts.locale);
      const suffix = qs.toString() ? `?${qs}` : '';
      return req<CollectionItem[]>(`/collections/${key}/items${suffix}`);
    },
    get: (key: string, id: string) => req<CollectionItem>(`/collections/${key}/items/${id}`),
    create: (key: string, body: { data: Record<string, unknown>; status?: 'draft' | 'published'; featured?: boolean; sortOrder?: number }) =>
      req<CollectionItem>(`/collections/${key}/items`, { method: 'POST', body: JSON.stringify(body) }),
    update: (key: string, id: string, body: { data?: Record<string, unknown>; status?: 'draft' | 'published'; featured?: boolean; sortOrder?: number }) =>
      req<CollectionItem>(`/collections/${key}/items/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
    delete: (key: string, id: string) =>
      req<{ ok: boolean }>(`/collections/${key}/items/${id}`, { method: 'DELETE' }),
  },

  media: {
    list: () => req<MediaItem[]>('/media'),
    upload: (file: File, folder?: 'properties' | 'agents' | 'posts' | 'settings' | 'pages' | 'general') => {
      const form = new FormData();
      form.append('file', file);
      if (folder) form.append('folder', folder);
      return fetch(`${BASE}/media/upload`, { method: 'POST', body: form, credentials: 'include' }).then(
        (r) => r.json() as Promise<MediaItem>,
      );
    },
    delete: (id: number) => req<{ ok: boolean }>(`/media/${id}`, { method: 'DELETE' }),
  },

  // Códigos QR — reutilizable por cualquier módulo. Un QR canónico por (tenant, targetUrl).
  qr: {
    list: (opts?: { module?: string; refId?: string }) => {
      const qs = new URLSearchParams();
      if (opts?.module) qs.set('module', opts.module);
      if (opts?.refId) qs.set('refId', opts.refId);
      return req<QrCodeRecord[]>(`/qr${qs.size ? `?${qs}` : ''}`);
    },
    save: (body: { file: Blob; module: string; refId?: string; label: string; targetUrl: string; styleJson: string }) => {
      const form = new FormData();
      form.append('file', body.file, 'qr.png');
      form.append('module', body.module);
      if (body.refId) form.append('refId', body.refId);
      form.append('label', body.label);
      form.append('targetUrl', body.targetUrl);
      form.append('styleJson', body.styleJson);
      return fetch(`${BASE}/qr`, { method: 'POST', body: form, credentials: 'include' }).then((r) => {
        if (!r.ok) return r.json().then((e) => { throw new Error((e as { error?: string }).error ?? r.statusText); });
        return r.json() as Promise<QrCodeRecord>;
      });
    },
    remove: (id: string) => req<{ ok: boolean }>(`/qr/${id}`, { method: 'DELETE' }),
  },

  settings: {
    get: () => req<SiteSettings | null>('/settings'),
    update: (body: Partial<SiteSettings>) =>
      req<SiteSettings>('/settings', { method: 'PUT', body: JSON.stringify(body) }),
    getTheme: () => req<Record<string, string>>('/settings/theme'),
    updateTheme: (body: Record<string, string>) =>
      req<{ ok: boolean }>('/settings/theme', { method: 'PUT', body: JSON.stringify(body) }),
    listUsers: () => req<User[]>('/settings/users'),
    createUser: (body: { email: string; password: string; role: 'admin' | 'editor' }) =>
      req<User>('/settings/users', { method: 'POST', body: JSON.stringify(body) }),
    deleteUser: (id: number) => req<{ ok: boolean }>(`/settings/users/${id}`, { method: 'DELETE' }),
    getSite: () => req<{
      name: string;
      slug: string;
      domain: string | null;
      deployHookUrl: string | null;
      pagesProject: string | null;
      deployAccountId: string | null;
      deployTokenSet: boolean;
      bookingTimezone: string;
      bookingsEnabled: boolean;
    }>('/settings/site'),
    updateSite: (body: {
      name?: string;
      domain?: string;
      deployHookUrl?: string;
      pagesProject?: string;
      deployAccountId?: string;
      deployToken?: string; // omit to leave unchanged; '' to clear
      bookingTimezone?: string;
      bookingsEnabled?: boolean;
    }) =>
      req('/settings/site', { method: 'PUT', body: JSON.stringify(body) }),
    capabilities: () => req<PanelCapabilities>('/settings/capabilities'),
    updateLocales: (locales: string[]) =>
      req<{ locales: string[]; multilingual: boolean }>('/settings/locales', {
        method: 'PUT',
        body: JSON.stringify({ locales }),
      }),
  },

  domains: {
    status: () => req<CloudflareDomainStatus>('/domains/status'),
    updateConfig: (body: {
      accountId?: string;
      apiToken?: string;
      primaryDomain?: string;
      transferState?: Record<string, TutorialProgressState>;
    }) => req<{ ok: boolean; primaryDomain: string | null; accountId: string | null; apiTokenConfigured: boolean }>('/domains/config', { method: 'PUT', body: JSON.stringify(body) }),
    search: (query: string) => req<{ query: string; results: DomainSearchResult[] }>('/domains/search', { method: 'POST', body: JSON.stringify({ query }) }),
    check: (domain: string) => req<DomainCheckResult>('/domains/check', { method: 'POST', body: JSON.stringify({ domain }) }),
    register: (domain: string) => req<{ domain: string; workflowState: string; completed: boolean; statusUrl: string | null; resourceUrl: string | null; message: string }>('/domains/register', { method: 'POST', body: JSON.stringify({ domain }) }),
    connect: (domain: string) => req<DomainConnectResult>('/domains/connect', { method: 'POST', body: JSON.stringify({ domain }) }),
    refresh: () => req<{ domain: string; pagesDomain: DomainConnectResult['pagesDomain']; message: string }>('/domains/refresh', { method: 'POST' }),
    updateRedirects: (body: RedirectPresetState) => req<RedirectPresetState>('/domains/redirects', { method: 'PUT', body: JSON.stringify(body) }),
    transferGuide: () => req<{ steps: Array<{ id: string; title: string; detail: string }> }>('/domains/transfer-guide'),
  },

  // Google Business — Nivel 1 (Places API): vincular la ficha y leer rating/reseñas.
  google: {
    get: () => req<GoogleBusinessRecord | null>('/google'),
    search: (q: string) =>
      req<{ results: GooglePlaceCandidate[] }>(`/google/search?q=${encodeURIComponent(q)}`),
    link: (placeId: string) =>
      req<GoogleBusinessRecord>('/google', { method: 'PUT', body: JSON.stringify({ placeId }) }),
    refresh: () => req<GoogleBusinessRecord>('/google/refresh', { method: 'POST' }),
    setVisibility: (showOnSite: boolean) =>
      req<GoogleBusinessRecord>('/google/visibility', { method: 'PUT', body: JSON.stringify({ showOnSite }) }),
    unlink: () => req<{ ok: boolean }>('/google', { method: 'DELETE' }),
  },

  publish: {
    trigger: () => req<{ logId: number; status: string }>('/publish', { method: 'POST' }),
    log: () => req<PublishLog[]>('/publish/log'),
    publishPage: (pageId: string) => req<Page>(`/publish/page/${pageId}`, { method: 'POST' }),
  },

  submissions: {
    list: () => req<FormSubmission[]>('/submissions'),
    stats: () => req<SubmissionStats>('/submissions/stats'),
    unreadCount: () => req<{ count: number }>('/submissions/unread-count'),
    markRead: () => req<{ ok: boolean }>('/submissions/mark-read', { method: 'POST' }),
    setAttended: (id: number, attended: boolean) =>
      req<{ ok: boolean; attendedAt: Date | null }>(`/submissions/${id}/attended`, {
        method: 'PATCH',
        body: JSON.stringify({ attended }),
      }),
  },

  analytics: {
    stats: () => req<VisitStats>('/analytics/stats'),
  },

  // Asistente del panel (Fase 2): telemetría del composer, ejecución de
  // acciones (con bitácora atómica en el servidor) y feed de actividad.
  assistant: {
    recordEvent: (body: AssistantEventInput) =>
      req<{ ok: boolean }>('/assistant/events', { method: 'POST', body: JSON.stringify(body) }),
    execute: (body: { intentId: string; param?: string }) =>
      req<AssistantExecuteResult>('/assistant/execute', { method: 'POST', body: JSON.stringify(body) }),
    activity: (limit = 8) => req<ActivityEntry[]>(`/assistant/activity?limit=${limit}`),
  },

  preview: {
    getUrl: (pageId: string) =>
      req<{ url: string }>(`/preview/${pageId}`, { method: 'POST' }),
    // Upsert the editor's unsaved block state for the live preview iframe.
    saveLive: (pageId: string, contentJson: string) =>
      req<{ ok: boolean }>(`/preview/live/${pageId}`, {
        method: 'PUT',
        body: JSON.stringify({ contentJson }),
      }),
  },

  // Generación de contenido con LLM (OpenRouter). El backend valida y limita por tenant.
  ai: {
    field: (collectionKey: string, fieldKey: string, opts?: { instruction?: string; itemContext?: Record<string, unknown> }) =>
      req<{ value: unknown }>('/ai/field', { method: 'POST', body: JSON.stringify({ collectionKey, fieldKey, ...opts }) }),
    item: (collectionKey: string, prompt?: string) =>
      req<{ data: Record<string, unknown> }>('/ai/item', { method: 'POST', body: JSON.stringify({ collectionKey, prompt }) }),
    seed: (collectionKey: string, count: number, businessContext?: string) =>
      req<{ created: number }>('/ai/seed', { method: 'POST', body: JSON.stringify({ collectionKey, count, businessContext }) }),
    block: (
      blockType: string,
      fieldName: string,
      opts?: {
        fieldLabel?: string;
        context?: string;
        locale?: string;
        instruction?: string;
        currentProps?: Record<string, unknown>;
        mode?: 'rewrite' | 'generate';
      },
    ) =>
      req<{ value: string }>('/ai/block', { method: 'POST', body: JSON.stringify({ blockType, fieldName, ...opts }) }),
    // Construye el sitio completo del tenant (Home + secciones + contacto + nav).
    site: (description?: string) =>
      req<{ pages: number }>('/ai/site', { method: 'POST', body: JSON.stringify({ description }) }),
    branding: (body: { instruction: string; industry?: string | null; currentTheme?: Record<string, unknown> }) =>
      req<{ theme: Record<string, unknown>; explanation: string; suggestionLabel: string }>(
        '/ai/branding',
        { method: 'POST', body: JSON.stringify(body) },
      ),
    image: (body: AiImageGenerateInput) =>
      req<AiImageGenerateResult>('/ai/image', { method: 'POST', body: JSON.stringify(body) }),
    // Fase 4: dispara la generación de imágenes de los bloques auto-elegibles del
    // sitio recién construido. No bloquea: el backend procesa en segundo plano.
    siteImages: () =>
      req<{ queued: number }>('/ai/site/images', { method: 'POST', body: JSON.stringify({}) }),
    siteImagesStatus: () =>
      req<{ pending: number; succeeded: number; failed: number; total: number }>('/ai/site/images/status'),
    // Cupo mensual de imágenes IA + si el plan lo incluye.
    imageUsage: () =>
      req<{ used: number; limit: number; month: string; allowed: boolean; plan: string | null }>('/ai/image/usage'),
    // Modo "Descríbela tú": preguntas de seguimiento para afinar antes de construir.
    pageQuestions: (pageName: string, description: string) =>
      req<{ questions: { question: string; options: string[] }[] }>(
        '/ai/page', { method: 'POST', body: JSON.stringify({ mode: 'describe', step: 'questions', pageName, description }) }),
    // Construye los bloques de una página (sin persistir). → contentJson.
    page: (opts: {
      mode: 'describe' | 'template';
      pageName: string;
      description?: string;
      templateKey?: string;
      answers?: { question: string; answer: string }[];
      bilingual?: boolean; // pide también la versión en inglés (contentJsonEn)
    }) =>
      req<{ contentJson: string; contentJsonEn?: string }>('/ai/page', { method: 'POST', body: JSON.stringify({ ...opts, step: 'build' }) }),
  },

  // Chatbot conversacional por tenant — config, uso y revisión de conversaciones.
  chatbot: {
    getSettings: () => req<ChatbotSettingsRecord>('/chatbot/settings'),
    updateSettings: (body: ChatbotSettingsInput) =>
      req<{ ok: boolean }>('/chatbot/settings', { method: 'PUT', body: JSON.stringify(body) }),
    usage: () => req<ChatbotUsageSummary>('/chatbot/usage'),
    conversations: () => req<ChatbotConversationSummary[]>('/chatbot/conversations'),
    conversation: (id: string) =>
      req<{ conversation: ChatbotConversationSummary; messages: ChatbotMessageRecord[] }>(`/chatbot/conversations/${id}`),
  },

  // Onboarding por industria — catálogo de presets + instanciación de tipos.
  onboarding: {
    catalog: () => req<OnboardingCatalog>('/onboarding/catalog'),
    // industry opcional: el wizard lo fija; el catálogo "añadir contenido" lo omite.
    instantiate: (typeKeys: string[], industry?: string) =>
      req<{ created: string[] }>('/onboarding/types', { method: 'POST', body: JSON.stringify({ industry, typeKeys }) }),
    complete: () => req<{ ok: boolean }>('/onboarding/complete', { method: 'POST' }),
    checklist: () => req<ChecklistStatus>('/onboarding/checklist'),
    markChecklist: (id: string, completed: boolean) =>
      req<{ ok: boolean }>('/onboarding/checklist', { method: 'PUT', body: JSON.stringify({ id, completed }) }),
  },

  tenants: {
    list: () => req<Array<{ id: string; name: string; slug: string; domain: string | null }>>('/tenants'),
  },

  setup: {
    check: () => fetch(`${BASE}/setup`, { credentials: 'include' }).then((r) => r.json()) as Promise<{ needsSetup: boolean }>,
    create: (email: string, password: string, tenantName: string, tenantSlug: string) =>
      req('/setup', { method: 'POST', body: JSON.stringify({ email, password, tenantName, tenantSlug }) }),
  },

  auth: {
    login: (email: string, password: string) => {
      clearActiveTenantId();
      return authReq('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
    },
    // Alta self-service: crea tenant + owner y deja la sesión iniciada en onboarding.
    signup: (email: string, password: string, businessName: string) => {
      clearActiveTenantId();
      return authReq<{
        user: { email: string; role: string; tenantId: string | null };
        tenant: { id: string; slug: string; domain: string | null };
        needsOnboarding: boolean;
      }>(
        '/signup', { method: 'POST', body: JSON.stringify({ email, password, businessName }) },
      );
    },
    logout: () => {
      clearActiveTenantId();
      return authReq('/auth/logout', { method: 'POST' });
    },
    me: async () => {
      const data = await fetch(`${BASE}/auth/me`, { credentials: 'include' }).then((r) => r.json()) as {
        user: { email: string; role: string; tenantId: string | null } | null;
        needsOnboarding?: boolean;
        onboardedAt?: number | null;
      };
      if (!data.user || data.user.role !== 'saas_admin') clearActiveTenantId();
      return data;
    },
    changePassword: (currentPassword: string, newPassword: string) =>
      req('/auth/password', { method: 'PUT', body: JSON.stringify({ currentPassword, newPassword }) }),
  },
};
